import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/best-life-insurance-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

/* ─── Carrier Rankings ───────────────────────────────────────────────────── */

const CARRIERS = [
  {
    rank: 1,
    name: 'Banner Life',
    amBest: 'A+',
    moody: 'A1',
    founded: 1949,
    bestFor: 'Best Overall Value — Lowest rates for healthy applicants',
    products: ['Term Life', 'Universal Life'],
    strengths: ['Consistently lowest term rates in Indianapolis', 'Accelerated underwriting up to $1M', 'No-exam approval in 24–48 hours', 'Strong financial stability'],
    weaknesses: ['Limited whole life options', 'No final expense products'],
    termRate: '$18/mo',
    termCoverage: '$500K · 20-yr · Age 35',
    badge: 'Best Overall',
    badgeColor: '#D97642',
    score: 9.8,
  },
  {
    rank: 2,
    name: 'Pacific Life',
    amBest: 'A+',
    moody: 'A1',
    founded: 1868,
    bestFor: 'Best for High Coverage — $1M+ policies with strong IUL options',
    products: ['Term Life', 'Universal Life', 'IUL', 'Whole Life'],
    strengths: ['Excellent IUL products for wealth building', 'Competitive rates for $1M+ coverage', 'Strong living benefits riders', 'Top-tier financial strength'],
    weaknesses: ['Slightly higher term rates than Banner', 'More complex product lineup'],
    termRate: '$19/mo',
    termCoverage: '$500K · 20-yr · Age 35',
    badge: 'Best for $1M+',
    badgeColor: '#1A3A52',
    score: 9.6,
  },
  {
    rank: 3,
    name: 'Protective Life',
    amBest: 'A+',
    moody: 'A2',
    founded: 1907,
    bestFor: 'Best for 30-Year Term — Lowest 30-year rates in Indianapolis',
    products: ['Term Life', 'Universal Life', 'Whole Life'],
    strengths: ['Best 30-year term rates in Indianapolis', 'No-lapse guarantee UL', 'Strong conversion options', 'Excellent customer service ratings'],
    weaknesses: ['Not the lowest for 10 or 20-year term', 'Limited IUL options'],
    termRate: '$20/mo',
    termCoverage: '$500K · 20-yr · Age 35',
    badge: 'Best 30-Year Term',
    badgeColor: '#4A7C59',
    score: 9.4,
  },
  {
    rank: 4,
    name: 'Mutual of Omaha',
    amBest: 'A+',
    moody: 'A1',
    founded: 1909,
    bestFor: 'Best for Seniors & Final Expense — Top-rated for ages 50–85',
    products: ['Term Life', 'Whole Life', 'Final Expense', 'Medicare Supplement'],
    strengths: ['Best final expense rates in Indianapolis', 'Strong senior product lineup', 'Excellent simplified issue options', 'Highly rated customer service'],
    weaknesses: ['Not the lowest for younger applicants', 'Limited IUL options'],
    termRate: '$22/mo',
    termCoverage: '$500K · 20-yr · Age 35',
    badge: 'Best for Seniors',
    badgeColor: '#D97642',
    score: 9.3,
  },
  {
    rank: 5,
    name: 'North American Company',
    amBest: 'A+',
    moody: 'A1',
    founded: 1886,
    bestFor: 'Best IUL — Top-rated indexed universal life for wealth building',
    products: ['Term Life', 'IUL', 'Whole Life', 'Annuities'],
    strengths: ['Best IUL products for cash value growth', 'Strong living benefits', 'Competitive term rates', 'Excellent annuity options'],
    weaknesses: ['Not the lowest for pure term coverage', 'Complex IUL products require guidance'],
    termRate: '$21/mo',
    termCoverage: '$500K · 20-yr · Age 35',
    badge: 'Best IUL',
    badgeColor: '#1A3A52',
    score: 9.2,
  },
  {
    rank: 6,
    name: 'Transamerica',
    amBest: 'A',
    moody: 'A3',
    founded: 1928,
    bestFor: 'Best for Health Conditions — Most lenient underwriting in Indianapolis',
    products: ['Term Life', 'Whole Life', 'Universal Life', 'Final Expense'],
    strengths: ['Most lenient underwriting for health conditions', 'Strong simplified issue options', 'Competitive rates for impaired risk', 'Good conversion options'],
    weaknesses: ['Slightly lower AM Best rating than top carriers', 'Not the lowest for preferred health class'],
    termRate: '$23/mo',
    termCoverage: '$500K · 20-yr · Age 35',
    badge: 'Best for Health Conditions',
    badgeColor: '#4A7C59',
    score: 9.0,
  },
];

/* ─── Best Carrier Matcher ───────────────────────────────────────────────── */

type MatchGoal = 'lowest-rate' | 'high-coverage' | 'senior' | 'health-conditions' | 'wealth-building' | 'no-exam';

function matchCarrier(goal: MatchGoal, age: number): { carrier: string; reason: string; rate: string; badge: string } {
  const matches: Record<MatchGoal, { carrier: string; reason: string; rate: string; badge: string }> = {
    'lowest-rate': { carrier: 'Banner Life', reason: 'Consistently offers the lowest term life rates in Indianapolis for healthy applicants. Accelerated underwriting with no exam up to $1M.', rate: age <= 35 ? '$18/mo' : age <= 45 ? '$36/mo' : '$68/mo', badge: 'Lowest Rate' },
    'high-coverage': { carrier: 'Pacific Life', reason: 'Best for $1M+ coverage with strong IUL options and top-tier financial strength. Excellent for high-income Indianapolis professionals.', rate: age <= 35 ? '$36/mo' : age <= 45 ? '$72/mo' : '$140/mo', badge: 'Best for $1M+' },
    'senior': { carrier: 'Mutual of Omaha', reason: 'Top-rated for Indianapolis seniors ages 50–85. Best final expense rates, simplified issue options, and guaranteed issue for any health condition.', rate: age <= 65 ? '$38/mo' : '$62/mo', badge: 'Best for Seniors' },
    'health-conditions': { carrier: 'Transamerica', reason: 'Most lenient underwriting in Indianapolis for controlled health conditions like diabetes, high blood pressure, and past cancer history.', rate: age <= 35 ? '$24/mo' : age <= 45 ? '$48/mo' : '$92/mo', badge: 'Best for Health Conditions' },
    'wealth-building': { carrier: 'North American Company', reason: 'Best IUL products for tax-advantaged wealth building. Market-linked cash value growth with downside protection — ideal for Indianapolis business owners and high earners.', rate: age <= 35 ? '$200/mo' : age <= 45 ? '$350/mo' : '$550/mo', badge: 'Best for Wealth Building' },
    'no-exam': { carrier: 'Banner Life', reason: 'Accelerated underwriting program approves up to $1M with no medical exam in 24–48 hours. Best no-exam rates for Indianapolis applicants under age 60.', rate: age <= 35 ? '$20/mo' : age <= 45 ? '$40/mo' : '$78/mo', badge: 'Best No-Exam' },
  };
  return matches[goal];
}

/* ─── FAQs ───────────────────────────────────────────────────────────────── */

const FAQS = [
  {
    q: 'What is the best life insurance company in Indianapolis?',
    a: 'The best life insurance company in Indianapolis depends on your specific situation. For lowest rates: Banner Life consistently offers the lowest term life rates for healthy Indianapolis applicants. For seniors: Mutual of Omaha is the top-rated carrier for final expense and simplified issue coverage. For health conditions: Transamerica has the most lenient underwriting. For wealth building: North American Company offers the best IUL products. For $1M+ coverage: Pacific Life is the top choice. As an independent agent, we compare all of these carriers simultaneously to find your best match.',
  },
  {
    q: 'How do I find the best life insurance rates in Indianapolis?',
    a: 'The best way to find the lowest life insurance rates in Indianapolis is to work with an independent agent who can compare multiple carriers simultaneously. Captive agents (State Farm, Allstate, Northwestern Mutual) can only offer their own company\'s rates. An independent agent like Hoosier Life Insurance compares 20+ carriers at once — Banner Life, Pacific Life, Protective Life, Mutual of Omaha, and more — to find your lowest rate. Most Indianapolis residents save $300–$800/year by comparing carriers instead of going with just one company.',
  },
  {
    q: 'Is State Farm or Northwestern Mutual the best life insurance in Indianapolis?',
    a: 'State Farm and Northwestern Mutual are reputable companies, but they are captive carriers — their agents can only sell their own products. State Farm\'s term life rates are typically 20–40% higher than the lowest available rates in Indianapolis. Northwestern Mutual focuses on whole life and doesn\'t offer the most competitive term rates. An independent agent compares 20+ carriers including Banner Life, Pacific Life, and Protective Life — which consistently offer lower rates than State Farm or Northwestern Mutual for most Indianapolis residents.',
  },
  {
    q: 'What AM Best rating should I look for in a life insurance company?',
    a: 'AM Best is the primary financial strength rating agency for insurance companies. For life insurance in Indianapolis, we recommend only carriers rated A or higher: A++ (Superior): Highest possible rating. A+ (Superior): Excellent financial strength — Banner Life, Pacific Life, Protective Life, Mutual of Omaha, North American. A (Excellent): Very strong — Transamerica, Foresters. A- (Excellent): Strong — acceptable for most coverage needs. We never recommend carriers rated below A- for life insurance in Indianapolis. All carriers we work with are rated A or higher by AM Best.',
  },
  {
    q: 'What is the best term life insurance in Indianapolis?',
    a: 'For term life insurance in Indianapolis, the best carrier depends on your age and term length: 10-year term: Banner Life or Pacific Life offer the lowest rates. 20-year term: Banner Life is typically the lowest for healthy applicants; Transamerica for those with health conditions. 30-year term: Protective Life consistently offers the lowest 30-year term rates in Indianapolis. For no-exam approval: Banner Life\'s accelerated underwriting program approves up to $1M in 24–48 hours. We compare all carriers to find your specific best rate.',
  },
  {
    q: 'What is the best whole life insurance in Indianapolis?',
    a: 'For whole life insurance in Indianapolis, the best carrier depends on your goal: For cash value growth: Pacific Life and North American Company offer the strongest whole life products. For final expense (seniors): Mutual of Omaha and Foresters Financial offer the best rates for $5K–$35K of permanent coverage. For guaranteed issue (any health): Gerber Life and AIG offer guaranteed acceptance for ages 50–85. For dividend-paying whole life: Guardian and MassMutual are the top mutual companies, though their rates are higher. We compare all options to find your best fit.',
  },
  {
    q: 'How much does the best life insurance cost in Indianapolis?',
    a: 'The best (lowest) life insurance rates in Indianapolis for a healthy non-smoker: Age 25: $500K 20-year term from $14/month (male) or $12/month (female). Age 35: $500K 20-year term from $18/month (male) or $15/month (female). Age 45: $500K 20-year term from $36/month (male) or $29/month (female). Age 55: $500K 20-year term from $94/month (male) or $76/month (female). These are Preferred health class rates from Banner Life. Rates vary by health class, carrier, and coverage amount. Call us for your exact personalized rate.',
  },
  {
    q: 'Is an independent agent better than going directly to a life insurance company in Indianapolis?',
    a: 'Yes — for most Indianapolis residents, working with an independent agent is significantly better than going directly to one company. An independent agent compares 20+ carriers simultaneously to find your lowest rate. A direct company agent can only offer their own products. The premium you pay is the same whether you go through an agent or directly — the agent\'s commission is paid by the carrier, not by you. The only reason to go directly to a company is if you already know exactly which carrier and product you want. Otherwise, an independent agent saves you time and money.',
  },
  {
    q: 'What is the best life insurance for Indianapolis families?',
    a: 'For Indianapolis families, the best life insurance is typically a 20-year term policy from Banner Life or Pacific Life. A healthy 35-year-old couple can get $500,000 each for approximately $18–$22/month per person — $36–$44/month combined for $1M of total family coverage. For families with young children, a 30-year term from Protective Life ensures coverage through college graduation. For families with health conditions, Transamerica offers the most lenient underwriting. We recommend separate policies for each spouse rather than a joint policy.',
  },
  {
    q: 'How do I get the best life insurance quote in Indianapolis?',
    a: 'Call Hoosier Life Insurance at (317) 648-2983 or use the Best Carrier Matcher on this page. As an independent agent, we compare 20+ carriers simultaneously to find your best rate. The process takes 15 minutes: we ask about your age, health, coverage need, and budget — then show you the top 3–5 carriers ranked by price. Most Indianapolis residents are surprised by how much they can save compared to what they\'re currently paying or what they\'ve been quoted by a single-company agent.',
  },
];

export default function BestLifeInsuranceIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [matchGoal, setMatchGoal] = useState<MatchGoal>('lowest-rate');
  const [matchAge, setMatchAge] = useState(35);
  const [matchResult, setMatchResult] = useState<{ carrier: string; reason: string; rate: string; badge: string } | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    const title = 'Best Life Insurance Indianapolis IN | Top Carrier Rankings 2026 | Hoosier Life';
    const description = 'Best life insurance companies in Indianapolis, Indiana. 2026 carrier rankings by AM Best rating, rates, and specialty. Best Carrier Matcher tool. Compare 20+ carriers — free quotes.';
    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: 'best life insurance Indianapolis, top life insurance companies Indianapolis, life insurance reviews Indianapolis Indiana, best life insurance rates Indianapolis' },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
    ];
    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });
    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          name: title,
          description,
          url: CANONICAL,
          dateModified: '2026-04-15T00:00:00Z',
        },
        {
          '@type': 'FAQPage',
          mainEntity: FAQS.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: 'Best Life Insurance Indianapolis', item: CANONICAL },
          ],
        },
      ],
    };
    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schema);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const handleMatch = () => {
    setMatchResult(matchCarrier(matchGoal, matchAge));
  };

  const goalOptions: { value: MatchGoal; label: string; icon: string }[] = [
    { value: 'lowest-rate', label: 'Lowest Monthly Rate', icon: 'ri-money-dollar-circle-line' },
    { value: 'high-coverage', label: '$1M+ Coverage', icon: 'ri-shield-star-line' },
    { value: 'senior', label: 'Senior / Final Expense', icon: 'ri-user-heart-line' },
    { value: 'health-conditions', label: 'Health Conditions', icon: 'ri-heart-pulse-line' },
    { value: 'wealth-building', label: 'Wealth Building (IUL)', icon: 'ri-plant-line' },
    { value: 'no-exam', label: 'No Medical Exam', icon: 'ri-timer-flash-line' },
  ];

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Best Life Insurance Indianapolis</span>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden min-h-[560px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/a6c1de21369a52ea5f473d6111f90a1a.jpg"
            alt="Best life insurance companies in Indianapolis Indiana"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/95 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-award-line text-sm"></i>
              2026 Rankings · Indianapolis, Indiana · Independent Analysis
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Best Life Insurance<br />
              <span className="text-[#D97642]">Indianapolis, Indiana</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              2026 rankings of the top life insurance companies in Indianapolis — by AM Best rating, rates, and specialty. Use our <strong className="text-white">Best Carrier Matcher</strong> to find the right company for your specific situation.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: '6', label: 'Top carriers ranked' },
                { value: 'A+', label: 'Minimum AM Best rating' },
                { value: '20+', label: 'Total carriers compared' },
                { value: '$0', label: 'Cost to compare' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="#matcher" className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-award-line text-lg"></i>
                Find My Best Carrier
              </a>
              <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content + Sidebar */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">

          {/* LEFT */}
          <div>

            {/* Best Carrier Matcher */}
            <div id="matcher" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Interactive Tool</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                Best Carrier Matcher — Indianapolis
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Tell us your primary goal and age — we'll match you with the best life insurance carrier in Indianapolis for your specific situation.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-award-line text-[#D97642]"></i>
                    Your Primary Goal
                  </h3>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
                    {goalOptions.map(opt => (
                      <button key={opt.value} onClick={() => { setMatchGoal(opt.value); setMatchResult(null); }}
                        className={`px-4 py-3 rounded-lg text-left transition-all cursor-pointer ${matchGoal === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                        <i className={`${opt.icon} text-lg block mb-1`}></i>
                        <div className="text-xs font-bold">{opt.label}</div>
                      </button>
                    ))}
                  </div>

                  <div className="mb-7">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-2 block">
                      Your Age: <span className="text-[#D97642] font-bold">{matchAge}</span>
                    </label>
                    <input type="range" min={20} max={75} step={1} value={matchAge}
                      onChange={e => { setMatchAge(Number(e.target.value)); setMatchResult(null); }}
                      className="w-full accent-[#D97642]" />
                    <div className="flex justify-between text-xs text-white/40 mt-1"><span>20</span><span>75</span></div>
                  </div>

                  <button onClick={handleMatch}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base">
                    <i className="ri-award-line text-lg"></i>
                    Find My Best Carrier in Indianapolis
                  </button>
                </div>

                {matchResult && (
                  <div className="px-7 py-7">
                    <div className="flex items-center gap-2 mb-4">
                      <i className="ri-award-fill text-[#D97642] text-2xl"></i>
                      <h3 className="text-base font-bold text-[#1A3A52]">Your Best Match — Age {matchAge}</h3>
                    </div>
                    <div className="p-5 bg-[#FFF5EF] border-2 border-[#D97642] rounded-xl mb-4">
                      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                        <div>
                          <span className="inline-block px-3 py-1 bg-[#D97642] text-white text-xs font-bold rounded-full mb-2">{matchResult.badge}</span>
                          <div className="text-xl font-extrabold text-[#1A3A52]">{matchResult.carrier}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-extrabold text-[#D97642]">{matchResult.rate}</div>
                          <div className="text-xs text-gray-500">estimated starting rate</div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">{matchResult.reason}</p>
                    </div>
                    <div className="p-4 bg-[#1A3A52] rounded-xl text-center">
                      <p className="text-white text-sm mb-3">Get your exact personalized rate from {matchResult.carrier} — free, no obligation.</p>
                      <button onClick={() => setShowBookingModal(true)}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all whitespace-nowrap cursor-pointer">
                        <i className="ri-calendar-check-fill"></i>
                        Get My Exact Rate — Free
                      </button>
                    </div>
                  </div>
                )}

                {!matchResult && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-award-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your primary goal and age above, then click <strong className="text-[#1A3A52]">Find My Best Carrier</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Carrier Rankings */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">2026 Rankings</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Top 6 Life Insurance Companies in Indianapolis — 2026
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                Ranked by financial strength, rates, and specialty. All carriers rated A or higher by AM Best. Rates shown are for a healthy 35-year-old male, $500K 20-year term, Preferred health class.
              </p>

              <div className="flex flex-col gap-5">
                {CARRIERS.map((carrier, i) => (
                  <div key={i} className={`bg-white rounded-xl border-2 p-6 ${i === 0 ? 'border-[#D97642]' : 'border-gray-100'}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 flex items-center justify-center rounded-xl font-extrabold text-lg ${i === 0 ? 'bg-[#D97642] text-white' : 'bg-[#1A3A52]/10 text-[#1A3A52]'}`}>
                          #{carrier.rank}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-lg font-extrabold text-[#1A3A52]">{carrier.name}</span>
                            <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white whitespace-nowrap" style={{ backgroundColor: carrier.badgeColor }}>{carrier.badge}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>AM Best: <strong className="text-green-600">{carrier.amBest}</strong></span>
                            <span>Founded: {carrier.founded}</span>
                            <span>Score: <strong className="text-[#D97642]">{carrier.score}/10</strong></span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-extrabold text-[#D97642]">{carrier.termRate}</div>
                        <div className="text-xs text-gray-400">{carrier.termCoverage}</div>
                      </div>
                    </div>
                    <p className="text-sm font-semibold text-[#1A3A52] mb-3">{carrier.bestFor}</p>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs font-bold text-green-600 uppercase tracking-wide mb-2">Strengths</p>
                        <ul className="flex flex-col gap-1">
                          {carrier.strengths.map(s => (
                            <li key={s} className="flex items-start gap-1.5 text-xs text-gray-600">
                              <i className="ri-check-line text-green-500 flex-shrink-0 mt-0.5"></i>{s}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-red-400 uppercase tracking-wide mb-2">Limitations</p>
                        <ul className="flex flex-col gap-1">
                          {carrier.weaknesses.map(w => (
                            <li key={w} className="flex items-start gap-1.5 text-xs text-gray-500">
                              <i className="ri-close-line text-red-300 flex-shrink-0 mt-0.5"></i>{w}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {carrier.products.map(p => (
                        <span key={p} className="text-xs bg-[#F9F7F4] text-gray-600 border border-gray-200 px-2 py-0.5 rounded-full">{p}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Independent vs Captive */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Why It Matters</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Independent Agent vs. Captive Agent — What Indianapolis Residents Need to Know
              </h2>
              <div className="grid md:grid-cols-2 gap-5">
                {[
                  {
                    type: 'Independent Agent (Hoosier Life)',
                    icon: 'ri-search-line',
                    color: '#D97642',
                    points: [
                      'Compares 20+ carriers simultaneously',
                      'Finds your lowest rate across all companies',
                      'No loyalty to any single carrier',
                      'Same premium — agent paid by carrier, not you',
                      'Can switch carriers if your needs change',
                      'Access to specialty carriers for health conditions',
                    ],
                    verdict: 'Best for most Indianapolis residents — saves $300–$800/year on average',
                    verdictColor: '#4A7C59',
                  },
                  {
                    type: 'Captive Agent (State Farm, Allstate, etc.)',
                    icon: 'ri-building-line',
                    color: '#9CA3AF',
                    points: [
                      'Can only offer their own company\'s products',
                      'Rates typically 20–40% higher than market',
                      'Loyal to their employer, not to you',
                      'Limited options for health conditions',
                      'Cannot shop around if rates increase',
                      'May not have the right product for your need',
                    ],
                    verdict: 'Only makes sense if you already know you want that specific company',
                    verdictColor: '#9CA3AF',
                  },
                ].map(item => (
                  <div key={item.type} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100" style={{ backgroundColor: `${item.color}10` }}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${item.color}20` }}>
                          <i className={`${item.icon} text-lg`} style={{ color: item.color }}></i>
                        </div>
                        <h3 className="text-sm font-bold text-[#1A3A52]">{item.type}</h3>
                      </div>
                    </div>
                    <div className="px-6 py-5">
                      <ul className="flex flex-col gap-2 mb-4">
                        {item.points.map(p => (
                          <li key={p} className="flex items-start gap-2 text-sm text-gray-600">
                            <i className="ri-arrow-right-s-line text-gray-400 flex-shrink-0 mt-0.5"></i>{p}
                          </li>
                        ))}
                      </ul>
                      <div className="p-3 rounded-lg" style={{ backgroundColor: `${item.verdictColor}10` }}>
                        <p className="text-xs font-bold" style={{ color: item.verdictColor }}>{item.verdict}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            {/* Internal Linking Cluster */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Indianapolis Life Insurance Guide</span>
              <h2 className="text-2xl font-bold text-[#1A3A52] mb-4 leading-tight">Explore All Indianapolis Life Insurance Topics</h2>
              <p className="text-sm text-gray-500 mb-6">Compare rates, explore coverage options, and find the right policy for your Indianapolis family.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { to: '/term-life-insurance-quotes-indianapolis', label: 'Term Life Insurance Quotes Indianapolis', icon: 'ri-calculator-line' },
                  { to: '/life-insurance-rates-indianapolis', label: 'Life Insurance Rates Indianapolis', icon: 'ri-line-chart-line' },
                  { to: '/cheap-life-insurance-indianapolis', label: 'Cheap Life Insurance Indianapolis', icon: 'ri-price-tag-3-line' },
                  { to: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance Indianapolis', icon: 'ri-money-dollar-circle-line' },
                  { to: '/life-insurance-quotes-indianapolis', label: 'Life Insurance Quotes Indianapolis', icon: 'ri-search-line' },
                  { to: '/whole-life-insurance-quotes-indianapolis', label: 'Whole Life Insurance Quotes Indianapolis', icon: 'ri-safe-line' },
                  { to: '/final-expense-insurance-quotes-indianapolis', label: 'Final Expense Insurance Quotes Indianapolis', icon: 'ri-heart-line' },
                  { to: '/no-exam-life-insurance-indianapolis', label: 'No Exam Life Insurance Indianapolis', icon: 'ri-stethoscope-line' },
                  { to: '/term-life-insurance-rates-indianapolis', label: 'Term Life Insurance Rates Indianapolis', icon: 'ri-bar-chart-line' },
                  { to: '/life-insurance-agent-indianapolis', label: 'Life Insurance Agent Indianapolis', icon: 'ri-user-star-line' },
                  { to: '/life-insurance-for-families-indianapolis', label: 'Life Insurance for Families Indianapolis', icon: 'ri-home-heart-line' },
                  { to: '/mortgage-life-insurance-indianapolis', label: 'Mortgage Life Insurance Indianapolis', icon: 'ri-building-line' },
                ].map(link => (
                  <Link key={link.to} to={link.to} className="flex items-center gap-3 px-4 py-3.5 bg-white rounded-xl border border-gray-100 hover:border-[#D97642]/30 hover:bg-[#FFF8F4] transition-all group cursor-pointer">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#1A3A52]">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Best Life Insurance Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug">{faq.q}</h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT SIDEBAR */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-award-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Find Your Best Carrier</h3>
                    <p className="text-xs text-white/70">20+ carriers compared · Free · No obligation</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold">
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                </div>
                <p className="text-xs text-gray-400 text-center mt-4">Free · No obligation · Same-day response</p>
              </div>
            </div>

            {/* Quick Rankings */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-award-line text-[#D97642]"></i>
                Quick Rankings — Indianapolis 2026
              </h3>
              <div className="flex flex-col gap-2">
                {[
                  { label: 'Best Overall', carrier: 'Banner Life', badge: '#D97642' },
                  { label: 'Best for $1M+', carrier: 'Pacific Life', badge: '#1A3A52' },
                  { label: 'Best 30-Year Term', carrier: 'Protective Life', badge: '#4A7C59' },
                  { label: 'Best for Seniors', carrier: 'Mutual of Omaha', badge: '#D97642' },
                  { label: 'Best IUL', carrier: 'North American', badge: '#1A3A52' },
                  { label: 'Best for Health Conditions', carrier: 'Transamerica', badge: '#4A7C59' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <p className="text-xs text-gray-500">{item.label}</p>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white whitespace-nowrap" style={{ backgroundColor: item.badge }}>{item.carrier}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free 15-Min Quote Call</h3>
                    <p className="text-xs text-white/70">We compare all 20+ carriers for you</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Find Your Best Carrier — Free',
                    step0Title: 'Pick a Date',
                    step1Title: 'Choose a Time',
                    step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date',
                    selectTime: 'See Available Times →',
                    reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev',
                    nextMonth: 'Next',
                    noSlotsAvailable: 'No slots available for this date',
                    selectedDateTime: 'Your Appointment',
                    modifyTime: 'Change',
                    customerName: 'Full Name',
                    customerPhone: 'Phone Number',
                    notes: "What are you looking for? (Optional)",
                    submitButton: 'Confirm My Free Carrier Comparison Call',
                    submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.',
                    backButton: '← Back to Calendar',
                    required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4">Related Pages</h3>
              <div className="flex flex-col gap-2">
                {[
                  { to: '/life-insurance-quotes-indianapolis', label: 'Compare Quotes — Indianapolis', icon: 'ri-search-line' },
                  { to: '/term-life-insurance-rates-indianapolis', label: 'Term Life Rate Table 2026', icon: 'ri-bar-chart-line' },
                  { to: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance', icon: 'ri-money-dollar-circle-line' },
                  { to: '/no-exam-life-insurance-indianapolis', label: 'No Medical Exam Options', icon: 'ri-timer-flash-line' },
                ].map(link => (
                  <Link key={link.to} to={link.to}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9F7F4] transition-colors group">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-[#1A3A52] font-medium">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Find the Best Life Insurance for You — Indianapolis
          </h2>
          <p className="text-base text-white/75 mb-7">
            We compare 20+ top-rated carriers to find your best match. Free, no obligation, same-day response.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#matcher" className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-award-line text-lg"></i>
              Find My Best Carrier
            </a>
            <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={127} productName="Best Life Insurance Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Best Life Insurance" quoteHref="/instant-quote" />

      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Find Your Best Life Insurance Carrier — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance"
                style={{ width: '100%', height: '600px', border: 'none' }} title="Book Appointment" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
